// src/hooks/useEpk.ts

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getEpkPage,
  createEpkPage,
  updateEpkPage,
  deleteEpkPage,
  getEpkUploadUrl,
  getEpkAnalytics,
  exportEpkSignups,
  getEpkSignupsEmailList,
  createEpkTrackableUrl,
  deleteEpkTrackableUrl,
} from '@/api/epk';
import type { EpkFormState } from '@/types/epk';

/** Fetch the current user's EPK page. */
export function useEpkPage() {
  return useQuery({
    queryKey: ['epk'],
    queryFn: getEpkPage,
    retry: (failureCount, error: any) => {
      // Don't retry on 404 (no page exists yet)
      if (error?.code === 'NOT_FOUND' || error?.status === 404) return false;
      return failureCount < 2;
    },
  });
}

/** Create a new EPK page. */
export function useCreateEpkPage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EpkFormState) => createEpkPage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epk'] });
    },
  });
}

/** Update the existing EPK page. */
export function useUpdateEpkPage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<EpkFormState>) => updateEpkPage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epk'] });
    },
  });
}

/** Delete the EPK page. */
export function useDeleteEpkPage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteEpkPage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epk'] });
    },
  });
}

/** Get a pre-signed upload URL. */
export function useGetEpkUploadUrl() {
  return useMutation({
    mutationFn: ({ filename, contentType }: { filename: string; contentType: string }) =>
      getEpkUploadUrl(filename, contentType),
  });
}

/** Fetch analytics data. */
export function useEpkAnalytics(range: '7d' | '30d' = '30d') {
  return useQuery({
    queryKey: ['epk-analytics', range],
    queryFn: () => getEpkAnalytics(range),
  });
}

/** Export signups as CSV. */
export function useExportEpkSignups() {
  return useMutation({
    mutationFn: async () => {
      const blob = await exportEpkSignups();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'epk-signups.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

/** Download email list as a text file. */
export function useDownloadEpkEmailList() {
  return useMutation({
    mutationFn: async () => {
      const emailList = await getEpkSignupsEmailList();
      const blob = new Blob([emailList], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'epk-signups-email-list.txt';
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

/** Create a trackable URL for the EPK page. */
export function useCreateEpkTrackableUrl() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createEpkTrackableUrl(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epk'] });
      queryClient.invalidateQueries({ queryKey: ['epk-analytics'] });
    },
  });
}

/** Delete a trackable URL from the EPK page. */
export function useDeleteEpkTrackableUrl() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (trackingId: string) => deleteEpkTrackableUrl(trackingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['epk'] });
      queryClient.invalidateQueries({ queryKey: ['epk-analytics'] });
    },
  });
}
